import React, { useEffect } from 'react';
import {
  Title,
  Text,
  Label,
  Select,
  Option,
  Input,
  Button,
  MessageStrip,
  Icon,
} from '@ui5/webcomponents-react';
import useAutoTranslate from '../../../hooks/useAutoTranslate';
import { CONFIG_STYLES } from '../configStyles';

function IntegrationsConfig({ formData, setFormData, systemSettings, onSave, isLoading }) {
  const integrationsSettings = systemSettings?.INTEGRATIONS;
  const { autoT } = useAutoTranslate();

  useEffect(() => {
    console.log('IntegrationsConfig - Current Values:', { formData, integrationsSettings });
  }, [formData, integrationsSettings]);

  if (!formData || !integrationsSettings) {
    return <div>{autoT('Cargando configuración de integraciones...')}</div>;
  }

  const handleSystemChange = (value) => {
    setFormData({ ...formData, selectedSystem: value });
  };

  const handleSapb1Change = (field, value) => {
    setFormData({ ...formData, sapb1: { ...formData.sapb1, [field]: value } });
  };

  const hasChanges = () => {
    const saved = integrationsSettings;
    if (formData.selectedSystem !== (saved.system?.default || 'none')) return true;
    if (formData.selectedSystem === 'sapb1') {
      const s = saved.sapb1 || {};
      return (
        formData.sapb1.baseUrl   !== (s.baseUrl?.default   || '') ||
        formData.sapb1.companyDb !== (s.companyDb?.default || '') ||
        formData.sapb1.userName  !== (s.userName?.default  || '') ||
        formData.sapb1.password  !== (s.password?.default  || '')
      );
    }
    return false;
  };

  const prepareDataForSave = () => ({
    ...integrationsSettings,
    system: { ...integrationsSettings.system, default: formData.selectedSystem },
    sapb1: {
      ...integrationsSettings.sapb1,
      enabled:   formData.selectedSystem === 'sapb1',
      baseUrl:   { ...integrationsSettings.sapb1.baseUrl,   default: formData.sapb1.baseUrl },
      companyDb: { ...integrationsSettings.sapb1.companyDb, default: formData.sapb1.companyDb },
      userName:  { ...integrationsSettings.sapb1.userName,  default: formData.sapb1.userName },
      password:  { ...integrationsSettings.sapb1.password,  default: formData.sapb1.password },
    },
  });

  const handleSave = () => onSave(prepareDataForSave());

  const systemOptions = integrationsSettings.system?.options || [];
  const isSapb1Selected = formData.selectedSystem === 'sapb1';

  return (
    <div style={CONFIG_STYLES.page}>
      {/* Header */}
      <div style={CONFIG_STYLES.headerRow}>
        <div style={CONFIG_STYLES.headerTitle}>
          <Title level="H3">{autoT('Integraciones')}</Title>
          <Text style={CONFIG_STYLES.labelHint}>
            {autoT('Configura las integraciones con sistemas externos')}
          </Text>
        </div>
        <Button
          design="Emphasized"
          onClick={handleSave}
          disabled={!hasChanges() || isLoading}
          icon="save"
        >
          {isLoading ? autoT('Guardando...') : autoT('Guardar Cambios')}
        </Button>
      </div>

      {hasChanges() && (
        <MessageStrip design="Information" style={{ marginBottom: '1rem' }}>
          {autoT('Hay cambios pendientes de guardar')}
        </MessageStrip>
      )}

      {/* Sistema de integración */}
      <div style={CONFIG_STYLES.sectionCard}>
        <div style={CONFIG_STYLES.sectionDivider}>
          <Title level="H4">{autoT('Sistema de Integración')}</Title>
        </div>
        <div style={{ maxWidth: '400px' }}>
          <div style={CONFIG_STYLES.fieldGroup}>
            <Label required>{autoT('Sistema')}</Label>
            <Select
              style={{ width: '100%' }}
              onChange={(e) => {
                const val =
                  e.detail.selectedOption.getAttribute('data-key') || e.detail.selectedOption.value;
                handleSystemChange(val);
              }}
            >
              {systemOptions.map(opt => (
                <Option key={opt.value} data-key={opt.value} selected={opt.value === formData.selectedSystem}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>
        </div>
      </div>

      {/* SAP B1 Service Layer */}
      {isSapb1Selected && (
        <div style={CONFIG_STYLES.sectionCard}>
          <div style={{
            ...CONFIG_STYLES.sectionDivider,
            borderLeft: '3px solid var(--sapHighlightColor)',
            paddingLeft: '0.75rem',
          }}>
            <Title level="H4">SAP B1 Service Layer</Title>
          </div>

          <div style={CONFIG_STYLES.formGrid}>
            <div style={CONFIG_STYLES.fieldGroup}>
              <Label required>{autoT('Base URL')}</Label>
              <Input
                style={{ width: '100%' }}
                placeholder="https://sapserver:50000/b1s/v1"
                value={formData.sapb1.baseUrl}
                onInput={(e) => handleSapb1Change('baseUrl', e.target.value)}
              />
            </div>

            <div style={CONFIG_STYLES.fieldGroup}>
              <Label required>CompanyDB</Label>
              <Input
                style={{ width: '100%' }}
                placeholder="SBO_EMPRESA"
                value={formData.sapb1.companyDb}
                onInput={(e) => handleSapb1Change('companyDb', e.target.value)}
              />
            </div>

            <div style={CONFIG_STYLES.fieldGroup}>
              <Label required>{autoT('Usuario')}</Label>
              <Input
                style={{ width: '100%' }}
                placeholder="manager"
                value={formData.sapb1.userName}
                onInput={(e) => handleSapb1Change('userName', e.target.value)}
              />
            </div>

            <div style={CONFIG_STYLES.fieldGroup}>
              <Label required>{autoT('Contraseña')}</Label>
              <Input
                type="Password"
                style={{ width: '100%' }}
                value={formData.sapb1.password}
                onInput={(e) => handleSapb1Change('password', e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Info */}
      <div style={CONFIG_STYLES.infoBox}>
        <div style={CONFIG_STYLES.infoHeader}>
          <Icon name="information" style={{ color: 'var(--sapHighlightColor)' }} />
          <Title level="H5">{autoT('Información')}</Title>
        </div>
        <ul style={{ color: 'var(--sapContent_LabelColor)', margin: 0, paddingLeft: '1.25rem', lineHeight: '1.8' }}>
          {isSapb1Selected ? (
            <>
              <li><Text>{autoT('La Base URL debe incluir el protocolo (https://) y el puerto')}</Text></li>
              <li><Text>{autoT('CompanyDB es el nombre de la base de datos de la empresa en SAP')}</Text></li>
              <li><Text>{autoT('Las credenciales se almacenan de forma segura en la base de datos')}</Text></li>
            </>
          ) : (
            <li><Text>{autoT('Seleccione un sistema de integración para configurar sus parámetros de conexión')}</Text></li>
          )}
        </ul>
      </div>
    </div>
  );
}

export default IntegrationsConfig;
